/**
 * One place that says what each `armed_status` value from
 * `GET /customer/{id}/about/` MEANS to an operator (SUP-515).
 *
 * The backend statuses (actuate_admin `MotionStatus`) are a mix of health
 * and transition states. Before this file the UI mapped them to a coloured
 * icon (sites list) and a locked toggle (site page) with hover-only
 * tooltips, so a site that reported DISARMING for half an hour while it was
 * running and alerting looked like "a greyed-out red switch" and nothing
 * else — the customer read it as "stuck armed" and could not disarm it.
 *
 * Every surface that shows arm state (site page toggle tooltip, About table
 * chip, sites list icon) reads from here so the wording stays consistent.
 */

export type ArmedStatusTone =
  | "armed"
  | "manual"
  | "transitional"
  | "warning"
  | "error"
  | "disarmed"
  | "unknown";

export interface ArmedStatusPresentation {
  /** Normalised backend status (upper-case), "" when absent. */
  status: string;
  /** Short label for a chip: "Arming…", "Armed – not running", … */
  label: string;
  tone: ArmedStatusTone;
  /** Plain-language explanation, used as the tooltip everywhere. */
  description: string;
  /** ARMING / DISARMING: the last command is still being applied. */
  transitional: boolean;
}

/**
 * The backend keeps an arm/disarm stamp for 10 minutes while the connector
 * restarts, so "a few moments" was under-promising in the old tooltips.
 */
export const ARMED_STATUS_SETTLE_HINT =
  "This normally settles within a couple of minutes, and at most 10 while the connector restarts.";

export const TRANSITIONAL_ARMED_STATUSES = ["ARMING", "DISARMING"];

export const isTransitionalArmedStatus = (armedStatus?: string): boolean =>
  TRANSITIONAL_ARMED_STATUSES.includes((armedStatus || "").toUpperCase());

export const getArmedStatusPresentation = (
  armedStatus?: string,
  armed?: boolean,
  active = true
): ArmedStatusPresentation => {
  const status = (armedStatus || "").toUpperCase();

  if (!active) {
    return {
      status,
      label: "Inactive",
      tone: "disarmed",
      description:
        "This site is inactive. It will not arm from its schedule until it is activated.",
      transitional: false,
    };
  }

  switch (status) {
    case "OK":
      return {
        status,
        label: "Armed",
        tone: "armed",
        description: "Armed on schedule and receiving motion from its cameras.",
        transitional: false,
      };
    case "ARMED_OUTSIDE_SCHEDULE":
      return {
        status,
        label: "Armed (manual)",
        tone: "manual",
        description:
          "Armed manually outside its schedule and generating alerts. It will disarm at the next scheduled disarm time, or as soon as you disarm it.",
        transitional: false,
      };
    case "WARNING":
      return {
        status,
        label: "Armed – no motion",
        tone: "warning",
        description:
          "Armed on schedule, but no motion has been seen in the last 10 minutes. The cameras may not be reachable.",
        transitional: false,
      };
    case "ERROR":
      return {
        status,
        label: "Armed – not running",
        tone: "error",
        description:
          "The schedule says this site should be armed, but no connector is running for it, so no alerts are being generated. Toggle it off and on to restart it, or contact support.",
        transitional: false,
      };
    case "ARMING":
      return {
        status,
        label: "Arming…",
        tone: "transitional",
        description: `Arm command received and the connector is restarting. Alerts resume once it is back up. ${ARMED_STATUS_SETTLE_HINT} You can still disarm the site while it arms.`,
        transitional: true,
      };
    case "DISARMING":
      return {
        status,
        label: "Disarming…",
        tone: "transitional",
        description: `Disarm command received and the connector is being stopped. ${ARMED_STATUS_SETTLE_HINT} If alerts keep arriving after that, arm and disarm the site again or contact support.`,
        transitional: true,
      };
    case "DISARMED_DURING_SCHEDULE":
      return {
        status,
        label: "Disarmed (manual)",
        tone: "disarmed",
        description:
          "Disarmed manually during its schedule. It will re-arm at the next scheduled arm time, or as soon as you arm it.",
        transitional: false,
      };
    case "NOT_RUNNING":
      if (armed) {
        // The list API sets `armed` for clips-style sites whose schedule is
        // the only signal; keep the old wording for that combination.
        return {
          status,
          label: "Armed – not running",
          tone: "warning",
          description:
            "This site is meant to be armed but is not currently running.",
          transitional: false,
        };
      }
      return {
        status,
        label: "Disarmed",
        tone: "disarmed",
        description:
          "Not armed. It will arm at the next scheduled arm time, or as soon as you arm it.",
        transitional: false,
      };
    case "UNKNOWN":
      return {
        status,
        label: "Unknown",
        tone: "unknown",
        description:
          "Armed status is unknown, or does not apply to this type of site (for example AI Link).",
        transitional: false,
      };
    default:
      return armed
        ? {
            status,
            label: "Armed",
            tone: "armed",
            description: "Armed.",
            transitional: false,
          }
        : {
            status,
            label: "Disarmed",
            tone: "disarmed",
            description:
              "Not armed. It will arm at the next scheduled arm time, or as soon as you arm it.",
            transitional: false,
          };
  }
};
